<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import AddPreference from './AddPreference.vue';
import EditPreference from './EditPreference.vue';
import PreferenceListItem from './PreferenceListItem.vue';
import ContactPreferenceDefinitionsAPI from 'dashboard/api/contactPreferenceDefinitions';

const { t } = useI18n();

const definitions = ref([]);
const isFetching = ref(false);
const searchQuery = ref('');
const selectedPreference = ref({});

const [showAddPopup, toggleAddPopup] = useToggle(false);
const [showEditPopup, toggleEditPopup] = useToggle(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);

const sortedDefinitions = computed(() =>
  [...definitions.value].sort((a, b) => a.position - b.position || a.id - b.id)
);

const filteredPreferences = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return sortedDefinitions.value;

  return picoSearch(sortedDefinitions.value, query, ['label', 'key']);
});

const nextPosition = computed(
  () => (sortedDefinitions.value.at(-1)?.position || 0) + 10
);

const fetchDefinitions = async () => {
  isFetching.value = true;
  try {
    const response = await ContactPreferenceDefinitionsAPI.getActive();
    definitions.value = response.data || [];
  } catch (error) {
    useAlert(t('CONTACT_PREFERENCES_SETTINGS.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const openAddPopup = () => {
  toggleAddPopup(true);
};

const hideAddPopup = () => {
  toggleAddPopup(false);
};

const handleCreatedPreference = preference => {
  definitions.value = [...definitions.value, preference];
};

const handleEditPreference = preference => {
  selectedPreference.value = preference;
  toggleEditPopup(true);
};

const hideEditPopup = () => {
  toggleEditPopup(false);
  selectedPreference.value = {};
};

const handleUpdatedPreference = preference => {
  definitions.value = definitions.value.map(item =>
    item.id === preference.id ? preference : item
  );
};

const handleToggleActive = async preference => {
  const nextActive = !preference.active;
  try {
    const response = await ContactPreferenceDefinitionsAPI.update(
      preference.id,
      { active: nextActive }
    );
    handleUpdatedPreference(response.data);
    useAlert(
      nextActive
        ? t('CONTACT_PREFERENCES_SETTINGS.STATUS.ACTIVATED')
        : t('CONTACT_PREFERENCES_SETTINGS.STATUS.DEACTIVATED')
    );
  } catch (error) {
    useAlert(t('CONTACT_PREFERENCES_SETTINGS.STATUS.ERROR'));
  }
};

const handleDeletePreference = preference => {
  selectedPreference.value = preference;
  toggleDeletePopup(true);
};

const closeDelete = () => {
  toggleDeletePopup(false);
  selectedPreference.value = {};
};

const confirmDeletePreference = async () => {
  try {
    await ContactPreferenceDefinitionsAPI.delete(selectedPreference.value.id);
    definitions.value = definitions.value.filter(
      item => item.id !== selectedPreference.value.id
    );
    useAlert(t('CONTACT_PREFERENCES_SETTINGS.DELETE.API.SUCCESS'));
    closeDelete();
  } catch (error) {
    useAlert(
      error?.message || t('CONTACT_PREFERENCES_SETTINGS.DELETE.API.ERROR')
    );
  }
};

onMounted(fetchDefinitions);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('CONTACT_PREFERENCES_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('CONTACT_PREFERENCES_SETTINGS.HEADER')"
        :description="$t('CONTACT_PREFERENCES_SETTINGS.DESCRIPTION')"
        :search-placeholder="
          $t('CONTACT_PREFERENCES_SETTINGS.SEARCH_PLACEHOLDER')
        "
      >
        <template v-if="sortedDefinitions?.length" #count>
          <span class="text-body-main text-n-slate-11 truncate min-w-0">
            {{
              $t('CONTACT_PREFERENCES_SETTINGS.COUNT', {
                n: sortedDefinitions.length,
              })
            }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('CONTACT_PREFERENCES_SETTINGS.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <span
          v-if="!filteredPreferences.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('CONTACT_PREFERENCES_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else-if="filteredPreferences.length"
          class="flex flex-col divide-y divide-n-weak border-t border-n-weak"
        >
          <PreferenceListItem
            v-for="preference in filteredPreferences"
            :key="preference.id"
            :preference="preference"
            @toggle-active="handleToggleActive"
            @edit="handleEditPreference"
            @delete="handleDeletePreference"
          />
        </div>
        <p
          v-else
          class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
        >
          {{ $t('CONTACT_PREFERENCES_SETTINGS.LIST.EMPTY_RESULT') }}
        </p>
      </div>
    </template>

    <AddPreference
      v-if="showAddPopup"
      v-model:show="showAddPopup"
      :on-close="hideAddPopup"
      :next-position="nextPosition"
      @created="handleCreatedPreference"
    />
    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditPreference
        :selected-preference="selectedPreference"
        @updated="handleUpdatedPreference"
        @on-close="hideEditPopup"
      />
    </woot-modal>
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="
        $t('CONTACT_PREFERENCES_SETTINGS.DELETE.CONFIRM.TITLE', {
          preferenceName: selectedPreference.label,
        })
      "
      :message="$t('CONTACT_PREFERENCES_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="`${$t('CONTACT_PREFERENCES_SETTINGS.DELETE.CONFIRM.YES')} ${
        selectedPreference.label || ''
      }`"
      :reject-text="$t('CONTACT_PREFERENCES_SETTINGS.DELETE.CONFIRM.NO')"
      :confirm-value="selectedPreference.label"
      :confirm-place-holder-text="
        $t('CONTACT_PREFERENCES_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
          preferenceName: selectedPreference.label,
        })
      "
      @on-confirm="confirmDeletePreference"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
